import React from 'react';
import { Box, Button, MenuItem, TextField, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';

import { DaySelect } from '../../DaySelect';
import { FullscreenDialog } from '../../dialogs/FullscreenDialog';
import { RecurrenceEnum } from '../../../__generated__/globalTypes';
import { CustomFrequencyPeriod, CustomFrequencyValue, DAY_OPTIONS } from './consts';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    label: {
      fontWeight: 700,
      fontSize: 12,
    },
    sectionLabel: {
      fontWeight: 700,
      fontSize: 12,
      borderBottom: '1px solid #9E9E9E',
      marginBottom: '12px',
      paddingBottom: '3px',
    },
    cancelButton: {
      color: theme.color.secondary.main700,
      backgroundColor: theme.color.primary.extraLight,
      borderRadius: 6,
      fontSize: 14,
    },
  })
);

const PERIODS: { value: CustomFrequencyPeriod; label: string }[] = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
];

const DAY_KEYS = DAY_OPTIONS.map((day) => day.value);

const DAY_SELECT_OPTIONS = 'MTWTFSS'.split('').map((day, dayIndex) => ({
  value: dayIndex,
  label: day,
}));

export const WEEK_OF_MONTH_OPTIONS = [
  { value: 1, label: '1st' },
  { value: 2, label: '2nd' },
  { value: 3, label: '3rd' },
  { value: 4, label: '4th' },
  { value: -1, label: 'Last' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  value: CustomFrequencyValue;
  onChange: (value: CustomFrequencyValue) => void;
}

export function CustomFrequencyDialog({ open, onClose, value, onChange }: Props) {
  const classes = useStyles();
  const [period, setPeriod] = React.useState<CustomFrequencyPeriod>(value.period);
  const [interval, setInterval] = React.useState<number>(value.interval);
  const [days, setDays] = React.useState<RecurrenceEnum[]>(value.days);
  const [weeksOfMonth, setWeeksOfMonth] = React.useState<number[]>(value.weeksOfMonth);

  React.useEffect(() => {
    if (open) {
      setPeriod(value.period);
      setInterval(value.interval);
      setDays(value.days);
      setWeeksOfMonth(value.weeksOfMonth);
    }
  }, [open]);

  const daysSelected = DAY_KEYS.reduce(
    (acc, day, dayIndex) => ({ ...acc, [dayIndex]: days.includes(day) }),
    {} as { [key: number]: boolean }
  );

  const toggleDay = (dayIndex: number) => {
    const day = DAY_KEYS[dayIndex];
    setDays(days.includes(day) ? days.filter((d) => d !== day) : DAY_KEYS.filter((d) => d === day || days.includes(d)));
  };

  const toggleWeekOfMonth = (week: number) => {
    setWeeksOfMonth(
      weeksOfMonth.includes(week)
        ? weeksOfMonth.filter((w) => w !== week)
        : WEEK_OF_MONTH_OPTIONS.map((o) => o.value).filter((w) => w === week || weeksOfMonth.includes(w))
    );
  };

  const isValid = interval >= 1 && (period !== 'months' || (days.length > 0 && weeksOfMonth.length > 0));

  const onDone = () => {
    onChange({
      period,
      interval,
      days: period === 'days' ? [] : days,
      weeksOfMonth: period === 'months' ? weeksOfMonth : [],
    });
  };

  return (
    <FullscreenDialog
      title='Custom Frequency'
      open={open}
      close={onClose}
      fullScreen={false}
      style={{ zIndex: 2200 }}
      dialogActions={() => (
        <React.Fragment>
          <Button variant='contained' className={classes.cancelButton} disableElevation onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onDone}
            disabled={!isValid}
            variant='contained'
            color='primary'
            style={{ flex: 1, borderRadius: '6px', fontSize: 14 }}
            disableElevation
          >
            Done
          </Button>
        </React.Fragment>
      )}
    >
      <Box>
        <Box display='flex' alignItems='center' marginTop='30px'>
          <Typography className={classes.label}>REPEAT EVERY</Typography>
          <TextField
            type='number'
            variant='outlined'
            size='small'
            value={interval}
            inputProps={{ min: 1 }}
            onChange={(event) => setInterval(Math.max(1, parseInt(event.target.value, 10) || 1))}
            style={{ marginLeft: 12, maxWidth: '5rem' }}
          />
          <TextField
            select
            variant='outlined'
            size='small'
            style={{ marginLeft: 12, minWidth: '6rem' }}
            value={period}
            onChange={(event) => setPeriod(event.target.value as CustomFrequencyPeriod)}
            SelectProps={{ MenuProps: { style: { zIndex: 2300 } } }}
          >
            {PERIODS.map(({ value: periodValue, label }) => (
              <MenuItem key={periodValue} value={periodValue}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {period !== 'days' && (
          <Box marginTop='30px'>
            <Typography className={classes.sectionLabel}>{period === 'months' ? 'ON' : 'DAYS'}</Typography>
            <DaySelect
              variant='primary'
              days={DAY_SELECT_OPTIONS}
              daysSelected={daysSelected}
              onSelectDay={toggleDay}
            />
          </Box>
        )}
        {period === 'months' && (
          <Box marginTop='30px'>
            <Typography className={classes.sectionLabel}>WEEK OF THE MONTH</Typography>
            <Box display='flex' style={{ gap: 8 }} flexWrap='wrap'>
              {WEEK_OF_MONTH_OPTIONS.map(({ value: week, label }) => (
                <Button
                  key={week}
                  size='small'
                  disableElevation
                  variant={weeksOfMonth.includes(week) ? 'contained' : 'outlined'}
                  color='primary'
                  onClick={() => toggleWeekOfMonth(week)}
                  style={{ textTransform: 'none', borderRadius: 6 }}
                >
                  {label}
                </Button>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </FullscreenDialog>
  );
}
